<script setup>
import { ref, onMounted, computed, watch, inject } from 'vue';
import { RequestService } from '../../services/RequestService'
import BcsSearchDropdown from '../BcsSearchDropdown.vue'
import BcsModal from '../BcsModal.vue'
import {
    getDatabase,
    ref as dbRef,
    get as dbGet
} from 'firebase/database'
import { getApp } from 'firebase/app'

const { handleErrors, put } = RequestService()
const functionsBaseUrl = inject('functionsBaseUrl')
const db = getDatabase(getApp())

const bcsData = ref({})
const bcsLoading = ref(false)
const bcsError = ref(null)
const expandedCategories = ref(new Set(['Announcements'])) 
const expandedSubcategories = ref(new Set())
const searchResults = ref([])
const isSearchActive = ref(false)
const flattenedCopies = ref([])
const selectedCopy = ref(null)
const copyModalRef = ref(null)
const copyModalMode = ref('view')
const successModalRef = ref(null)
const editingCopy = ref(null)
const faqSections = ref([])
const remoteFaqs = ref([])
const displayedFaqSections = ref([])
const flattenedFaqs = ref([])
const glossaryData = ref([])
const flattenedGlossary = ref([])
const landingPageData = ref({})
const flattenedLandingPage = ref([])
const editSaving = ref(false)
const editForm = ref({
  id: '',
  name: '',
  caption: '',
  default_value: ''
})

const createFaqSaving = ref(false)
const createFaqForm = ref({
  title: '',
  body: '',
  sectionKey: ''
})

const notificationModalMode = ref('success')
const deletingFaq = ref(null)
const deleteInProgress = ref(false)

const isFormValid = computed(() => {
  return editForm.value.name.trim() !== '' && 
         editForm.value.default_value.trim() !== ''
})

const isCreateFaqFormValid = computed(() => {
  return createFaqForm.value.title.trim() !== '' &&
         createFaqForm.value.body.trim() !== ''
})

const allUniqueItems = computed(() => {
  return [
    ...flattenedCopies.value,
    ...flattenedFaqs.value,
    ...flattenedGlossary.value,
    ...flattenedLandingPage.value
  ]
})

function isCopy(obj) {
  return obj && typeof obj === 'object' && 
         // eslint-disable-next-line no-prototype-builtins
         (obj.hasOwnProperty('caption') || obj.hasOwnProperty('default_value') || obj.hasOwnProperty('name') || obj.hasOwnProperty('id'))
}

async function saveCopy(path, data) {
  const response = await put(
    'saveCopy',
    `${functionsBaseUrl}/utilityGateway/api/v1/copy-manager/copy`,
    { path, data }
  )
  if (!response) {
    throw new Error('Copy save failed')
  }
  return response
}

function processHierarchy(data, path = []) {
  const result = []
  
  Object.keys(data).forEach(key => {
    const item = data[key]
    const currentPath = [...path, key]
    
    if (isCopy(item)) {
      result.push({
        type: 'copy',
        key: key,
        path: currentPath,
        data: { ...item, key }
      })
    } else if (typeof item === 'object' && item !== null) {
      const children = processHierarchy(item, currentPath)
      const copies = children.filter(child => child.type === 'copy')
      const subcategories = children.filter(child => child.type === 'category')
      
      if (copies.length > 0 || subcategories.length > 0) {
        result.push({
          type: 'category',
          key: key,
          path: currentPath,
          name: key,
          copies: copies,
          subcategories: subcategories,
          totalCopies: copies.length + subcategories.reduce((sum, sub) => sum + sub.totalCopies, 0)
        })
      }
    }
  })
  
  return result
}

const categorizedData = computed(() => {
  const bcsCategories = []
  
  if (bcsData.value && Object.keys(bcsData.value).length > 0) {
    bcsCategories.push(...processHierarchy(bcsData.value).filter(item => item.type === 'category'))
  }
  
  if (flattenedFaqs.value.length > 0) {
    const faqCopies = flattenedFaqs.value.map(faq => ({
      type: 'copy',
      key: faq.MIGX_id || faq.id || 'faq',
      path: ['FAQs', faq.MIGX_id || faq.id || 'faq'],
      data: {
        id: faq.MIGX_id || faq.id || '',
        name: faq.faqTitle || faq.name || 'FAQ Item',
        caption: faq.sectionId || faq.caption || 'FAQ',
        default_value: faq.faqBody || faq.default_value || '',
        key: faq.MIGX_id || faq.id || 'faq',
        questionTitle: faq.questionTitle || ''
      }
    }))
    
    bcsCategories.push({
      type: 'category',
      key: 'FAQs',
      path: ['FAQs'],
      name: 'FAQs',
      copies: faqCopies,
      subcategories: [],
      totalCopies: faqCopies.length
    })
  }
  
  if (flattenedGlossary.value.length > 0) {
    const glossaryCopies = flattenedGlossary.value.map(item => ({
      type: 'copy',
      key: item.MIGX_id || item.id || 'glossary',
      path: ['Glossary', item.MIGX_id || item.id || 'glossary'],
      data: {
        id: item.MIGX_id || item.id || '',
        name: item.title || item.name || 'Glossary Item',
        caption: item.subtitle || item.caption || 'Glossary',
        default_value: item.body || item.default_value || '',
        key: item.MIGX_id || item.id || 'glossary'
      }
    }))
    
    bcsCategories.push({
      type: 'category',
      key: 'Glossary',
      path: ['Glossary'],
      name: 'Glossary',
      copies: glossaryCopies,
      subcategories: [],
      totalCopies: glossaryCopies.length
    })
  }
  
  if (flattenedLandingPage.value.length > 0) {
    const sectionGroups = {}
    
    flattenedLandingPage.value.forEach(item => {
      const sectionName = item.path[1]
      if (!sectionGroups[sectionName]) {
        sectionGroups[sectionName] = []
      }
      
      sectionGroups[sectionName].push({
        type: 'copy',
        key: item.id,
        path: item.path,
        data: {
          id: item.id,
          name: item.name,
          caption: item.caption,
          default_value: item.default_value,
          key: item.id
        }
      })
    })
    
    const subcategories = Object.entries(sectionGroups).map(([sectionName, copies]) => ({
      type: 'category',
      key: sectionName,
      path: ['Landing Page', sectionName],
      name: sectionName,
      copies: copies,
      subcategories: [],
      totalCopies: copies.length
    }))
    
    bcsCategories.push({
      type: 'category',
      key: 'Landing Page',
      path: ['Landing Page'],
      name: 'Landing Page',
      copies: [],
      subcategories: subcategories,
      totalCopies: flattenedLandingPage.value.length
    })
  }
  
  return isSearchActive.value ? filterCategoriesBySearch(bcsCategories) : bcsCategories
})

const totalCopies = computed(() => {
  return categorizedData.value.reduce((total, category) => total + category.totalCopies, 0)
})

const emit = defineEmits(['update:totalCopies'])

watch(totalCopies, (newTotal) => {
  emit('update:totalCopies', newTotal)
}, { immediate: true })

function createFlattenedLandingPage() {
  const landingPageCopies = []
    
  if (landingPageData.value && typeof landingPageData.value === 'object') {
    Object.entries(landingPageData.value).forEach(([sectionKey, sectionData]) => {
      
      if (sectionData && typeof sectionData === 'object') {
        
        if (sectionKey === 'FAQs' && sectionData['A-Main']?.FaqList) {
          try {
            const faqList = JSON.parse(sectionData['A-Main'].FaqList)
            faqList.forEach(faq => {
              landingPageCopies.push({
                id: faq.MIGX_id || '',
                name: faq.faqTitle || 'FAQ Item',
                caption: 'FAQ',
                default_value: faq.faqBody || '',
                path: ['Landing Page', 'FAQs', faq.MIGX_id || 'faq'],
                pathString: `Landing Page > FAQs > ${faq.faqTitle || 'FAQ Item'}`
              })
            })
          } catch (e) {
            handleErrors('Error parsing FAQ list:', e)
          }
        }
        
        else if (sectionKey === 'OurProduct') {
          if (sectionData['accordion-items']?.['A-Main']?.ourProductList) {
            try {
              const productList = JSON.parse(sectionData['accordion-items']['A-Main'].ourProductList)
              productList.forEach(product => {
                landingPageCopies.push({
                  id: product.MIGX_id || '',
                  name: product.ourProductTitle || 'Product Item',
                  caption: 'Our Product',
                  default_value: product.ourProductBody || '',
                  path: ['Landing Page', 'OurProduct', 'accordion-items', product.MIGX_id || 'product'],
                  pathString: `Landing Page > OurProduct > ${product.ourProductTitle || 'Product Item'}`
                })
              })
            } catch (e) {
              handleErrors('Error parsing product list:', e)
            }
          }
          
          if (sectionData.items && Array.isArray(sectionData.items)) {
            sectionData.items.forEach((item, index) => {
              landingPageCopies.push({
                id: item.id || `${index + 1}`,
                name: item.title || `Product Item ${index + 1}`,
                caption: 'Our Product',
                default_value: item.subtitle || '',
                path: ['Landing Page', 'OurProduct', item.id || `${index + 1}`],
                pathString: `Landing Page > OurProduct > ${item.title || 'Product Item'}`
              })
            })
          }
        }
        
        else if (sectionData.items && Array.isArray(sectionData.items)) {
          sectionData.items.forEach((item, index) => {
            let itemName = ''
            let itemContent = ''
            
            switch (sectionKey) {
              case 'Founders':
                itemName = item.name || `Founder ${index + 1}`
                itemContent = item.quote || ''
                break
              case 'Testimonials':
                itemName = `${item.author || 'Author'} - ${item.company || 'Company'}`
                itemContent = item.quote || ''
                break
              case 'WhyBCSPro':
                itemName = item.title || `Why BCS Pro ${index + 1}`
                itemContent = item.subtitle || ''
                break
              default:
                itemName = item.title || item.name || `${sectionKey} Item ${index + 1}`
                itemContent = item.subtitle || item.quote || item.role || ''
            }
            
            landingPageCopies.push({
              id: item.id || `${index + 1}`,
              name: itemName,
              caption: sectionKey,
              default_value: itemContent,
              path: ['Landing Page', sectionKey, item.id || `${index + 1}`],
              pathString: `Landing Page > ${sectionKey} > ${itemName}`
            })
          })
        }
        
        else if (sectionData['Labels - Subtitles']) {
          Object.entries(sectionData['Labels - Subtitles']).forEach(([key, labelData]) => {
            landingPageCopies.push({
              id: labelData.id || key,
              name: labelData.name || key,
              caption: sectionKey,
              default_value: labelData.default_value || '',
              path: ['Landing Page', sectionKey, key], 
              pathString: `Landing Page > ${sectionKey} > ${labelData.name || key}`
            })
          })
        }
        
        else {
          Object.entries(sectionData).forEach(([key, value]) => {
            if (value && typeof value === 'object' && (value.name || value.default_value)) {
              landingPageCopies.push({
                id: value.id || key,
                name: value.name || key,
                caption: sectionKey,
                default_value: value.default_value || '',
                path: ['Landing Page', sectionKey, key],
                pathString: `Landing Page > ${sectionKey} > ${value.name || key}`
              })
            }
          })
        }
      }
    })
  }
  
  flattenedLandingPage.value = landingPageCopies
}

async function getTemplateVariables() {
  try {
    bcsLoading.value = true
    bcsError.value = null
    
    const references = [
      dbRef(db, 'BCS'),
      dbRef(db, 'FAQS'),
      dbRef(db, 'GLOSSARY'),
      dbRef(db, 'LANDING_PAGE')
    ]
    
    const snapshots = await Promise.all(references.map(dbGet))
    
    const [bcsSnapshot, faqsSnapshot, glossarySnapshot, landingPageSnapshot] = snapshots
    
    if (bcsSnapshot.exists()) {
      bcsData.value = bcsSnapshot.val()
      remoteFaqs.value = faqsSnapshot.val()
      glossaryData.value = glossarySnapshot.val()
      landingPageData.value = landingPageSnapshot.val()
      
      createFlattenedCopies()
      createFlattenedFaqs()
      createFlattenedGlossary()
      createFlattenedLandingPage()
    } else {
      bcsData.value = {}
    }
    
    bcsLoading.value = false
    return bcsData.value
  } catch (error) {
    bcsError.value = 'Error loading BCS data'
    bcsLoading.value = false
    handleErrors('Error fetching BCS data:', error)
  }
}

function createFlattenedGlossary() {
  if (glossaryData.value && typeof glossaryData.value === 'object') {

    flattenedGlossary.value = Object.entries(glossaryData.value).map(([key, item]) => {
      
      return {
        id: item.MIGX_id || key,
        name: item.title || item.name || key,
        caption: item.subtitle || item.caption || 'Glossary',
        default_value: item.body || item.content || item.default_value || '',
        path: ['Glossary', key],
        pathString: `Glossary > ${item.title || item.name || key}`,
        MIGX_id: item.MIGX_id || key,
        title: item.title || item.name || key,
        body: item.body || item.content || item.default_value || '',
        subtitle: item.subtitle || item.caption || ''
      }
    })
    
  }
}

function createFlattenedFaqs() {
  faqSections.value = Object.entries(remoteFaqs.value).map(([key, section]) => ({
      ...section,
      questionTitle: key,
      FaqList: section.FaqList ? JSON.parse(section.FaqList) : []
    }))
    
  displayedFaqSections.value = [...faqSections.value]
  
  flattenedFaqs.value = faqSections.value.flatMap(section =>
    section.FaqList.map(faq => ({
      id: faq.MIGX_id || '',
      name: faq.faqTitle || 'FAQ Item',
      caption: section.sectionId || faq.sectionId || 'FAQ',
      default_value: faq.faqBody || '',
      path: ['FAQs', faq.MIGX_id || 'faq'],
      pathString: `FAQs > ${faq.faqTitle || 'FAQ Item'}`,
      
      MIGX_id: faq.MIGX_id || '',
      faqTitle: faq.faqTitle || 'FAQ Item',
      faqBody: faq.faqBody || '',
      sectionId: section.sectionId || faq.sectionId || 'FAQ',
      questionTitle: section.questionTitle,
      ...faq 
    }))
  )

}

function createFlattenedCopies() {
  const copies = []
  
  function extractCopies(data, path = []) {
    if (!data) return
    
    Object.keys(data).forEach(key => {
      const item = data[key]
      const currentPath = [...path, key]
      
      if (isCopy(item)) {
        copies.push({
          id: item.id || key,
          name: item.name || key,
          caption: item.caption || '',
          default_value: item.default_value || '',
          path: currentPath,
          pathString: currentPath.join(' > ')
        })
      } else if (typeof item === 'object' && item !== null) {
        extractCopies(item, currentPath)
      }
    })
  }
  
  extractCopies(bcsData.value)
  flattenedCopies.value = copies
}

function toggleCategory(categoryName) {
  if (expandedCategories.value.has(categoryName)) {
    expandedCategories.value.delete(categoryName)
  } else {
    expandedCategories.value.add(categoryName)
  }
}

function toggleSubcategory(path) {
  const pathString = path.join('.')
  if (expandedSubcategories.value.has(pathString)) {
    expandedSubcategories.value.delete(pathString)
  } else {
    expandedSubcategories.value.add(pathString)
  }
}

function handleEdit(copy, path) {
  editingCopy.value = { ...copy, path }
  editForm.value = {
    id: copy.data.id || '',
    name: copy.data.name || copy.key,
    caption: copy.data.caption || '',
    default_value: copy.data.default_value || ''
  }
  copyModalMode.value = 'edit'
  copyModalRef.value.openModal()
}

async function handleSaveEdit() {
  if (!editingCopy.value) return
  
  try {
    editSaving.value = true
    const updateData = {
      caption: editForm.value.caption,
      default_value: editForm.value.default_value,
      id: editForm.value.id,
      name: editForm.value.name
    }
    
    const path = editingCopy.value.path
    let firebasePath = ''
    
    const arraySections = ['Founders', 'Testimonials', 'WhyBCSPro', 'OurProduct']
    
    if (path[0] === 'FAQs') {
      await updateFAQ(editingCopy.value, editForm.value)
      copyModalRef.value.closeModal()
      successModalRef.value.openModal()
      return
    } else if (path[0] === 'Landing Page' && path[1] === 'FAQs') {
      await updateFAQ(editingCopy.value, editForm.value)
      copyModalRef.value.closeModal()
      successModalRef.value.openModal()
      return
    } else if (path[0] === 'Landing Page' && arraySections.includes(path[1])) {
      const sectionKey = path[1]
      
      if (sectionKey === 'OurProduct' && path[2] === 'accordion-items') {
        const productId = path[3] 
        
        const section = landingPageData.value[sectionKey]
        if (section && section['accordion-items']?.['A-Main']?.ourProductList) {
          try {
            const productList = JSON.parse(section['accordion-items']['A-Main'].ourProductList)
            const productIndex = productList.findIndex(product => product.MIGX_id === productId)
            
            if (productIndex !== -1) {
              productList[productIndex] = {
                ...productList[productIndex],
                ourProductTitle: editForm.value.name,
                ourProductBody: editForm.value.default_value
              }
              
              const updatedProductListJson = JSON.stringify(productList)
              firebasePath = `LANDING_PAGE/OurProduct/accordion-items/A-Main`
              const updateObject = {
                ourProductList: updatedProductListJson
              }

              await saveCopy(firebasePath, updateObject)
              
              section['accordion-items']['A-Main'].ourProductList = updatedProductListJson
              createFlattenedLandingPage()
              
            }
          } catch (e) {
            handleErrors('Error updating OurProduct accordion-items:', e)
          }
        }
        
        copyModalRef.value.closeModal()
        successModalRef.value.openModal()
        return
      } else {
        updateLocalDataByType(path, editForm.value)
        
        const section = landingPageData.value[sectionKey]
        if (section && section.items && Array.isArray(section.items)) {
          firebasePath = `LANDING_PAGE/${sectionKey}/items`

          const itemsObject = {}
          section.items.forEach((item, index) => {
            itemsObject[index] = item
          })

          await saveCopy(firebasePath, itemsObject)
        }
      }
      
      copyModalRef.value.closeModal()
      successModalRef.value.openModal()
      return
    } else if (path[0] === 'Glossary') {
      const glossaryKey = path[1]
      firebasePath = `GLOSSARY/${glossaryKey}`
      
      const glossaryUpdateData = {
        MIGX_id: updateData.id,
        body: updateData.default_value,
        subtitle: updateData.caption
      }

      await saveCopy(firebasePath, glossaryUpdateData)
    } else if (path[0] === 'Landing Page') {
      const sectionKey = path[1] 
      const elementKey = path[2]  
      
      const section = landingPageData.value[sectionKey]
      if (section && section['Labels - Subtitles'] && section['Labels - Subtitles'][elementKey]) {
        firebasePath = `LANDING_PAGE/${sectionKey}/Labels - Subtitles/${elementKey}`

        await saveCopy(firebasePath, updateData)
      } else {
        firebasePath = `LANDING_PAGE/${path.slice(1).join('/')}`

        await saveCopy(firebasePath, updateData)
      }
    } else {
      firebasePath = `BCS/${path.join('/')}`

      await saveCopy(firebasePath, updateData)
    }
    
    if (!(path[0] === 'Landing Page' && arraySections.includes(path[1]))) {
      updateLocalDataByType(path, editForm.value)
    }
    
    copyModalRef.value.closeModal()
    successModalRef.value.openModal()
  } catch (error) {
    handleErrors('Error saving copy:', error)
  } finally {
    editSaving.value = false
  }
}

async function updateFAQ(editingCopy, newData) {
  try {
    
    const faqId = editingCopy.data.id
    let targetSection = null
    let targetFaqIndex = -1    
    if (editingCopy.path && editingCopy.path[0] === 'Landing Page' && editingCopy.path[1] === 'FAQs') {
      
      if (landingPageData.value.FAQs && landingPageData.value.FAQs['A-Main'] && landingPageData.value.FAQs['A-Main'].FaqList) {
        try {
          const faqList = JSON.parse(landingPageData.value.FAQs['A-Main'].FaqList)
          targetFaqIndex = faqList.findIndex(faq => faq.MIGX_id === faqId)
          
          if (targetFaqIndex !== -1) {
            faqList[targetFaqIndex] = {
              ...faqList[targetFaqIndex],
              faqTitle: newData.name,
              faqBody: newData.default_value
            }
            
            const firebasePath = `LANDING_PAGE/FAQs/A-Main`
            const updatedFaqListJson = JSON.stringify(faqList)

            const updateObject = {
              FaqList: updatedFaqListJson
            }

            await saveCopy(firebasePath, updateObject)
            
            landingPageData.value.FAQs['A-Main'].FaqList = updatedFaqListJson
            createFlattenedLandingPage()
            
            return
          }
        } catch (e) {
          handleErrors('Error parsing Landing Page FAQ list:', e)
        }
      }
    } else {      
      const sectionKey = editingCopy.data.questionTitle
      const targetSection = faqSections.value.find(s => s.questionTitle === sectionKey)
      const targetFaqIndex = targetSection ? targetSection.FaqList.findIndex(faq => faq.MIGX_id === faqId) : -1

      if (!targetSection || targetFaqIndex === -1) {
       handleErrors('FAQ not found in any section')
      }
      

      targetSection.FaqList[targetFaqIndex] = {
        ...targetSection.FaqList[targetFaqIndex],
        faqTitle: newData.name,
        faqBody: newData.default_value
      }
      
      const firebasePath = `FAQS/${targetSection.questionTitle}`
      const updatedFaqListJson = JSON.stringify(targetSection.FaqList)

      const updateObject = {
        FaqList: updatedFaqListJson
      }

      await saveCopy(firebasePath, updateObject)
      
      remoteFaqs.value[targetSection.questionTitle].FaqList = updatedFaqListJson
      createFlattenedFaqs()
      
    }
    
  } catch (error) {
    handleErrors('Error updating FAQ:', error)
  }
}

function updateLocalDataByType(path, newData) {
 
  if (path[0] === 'FAQs') {
    console.log('Regular FAQ local data already updated in updateFAQ function')
  } else if (path[0] === 'Landing Page' && path[1] === 'FAQs') {
    console.log('Landing Page FAQ local data already updated in updateFAQ function')
  } else if (path[0] === 'Glossary') {
    const glossaryKey = path[1]
    
    if (glossaryData.value[glossaryKey]) {
      const updatedGlossaryItem = {
        ...glossaryData.value[glossaryKey],
        body: newData.default_value || glossaryData.value[glossaryKey].body,
        subtitle: newData.caption || glossaryData.value[glossaryKey].subtitle,
        MIGX_id: newData.id || glossaryData.value[glossaryKey].MIGX_id
      }
      
      glossaryData.value[glossaryKey] = updatedGlossaryItem
      createFlattenedGlossary()
      
    } else {
      handleErrors(`Glossary key "${glossaryKey}" not found in glossaryData`)
    }
  } else if (path[0] === 'Landing Page') {
    const sectionKey = path[1] 
    const itemId = path[2] 
    
    const section = landingPageData.value[sectionKey]
    
    if (section && section.items && Array.isArray(section.items)) {
      let itemIndex = -1
      
      itemIndex = section.items.findIndex(item => item.id === itemId)
      
      if (itemIndex === -1 && !isNaN(parseInt(itemId))) {
        const index = parseInt(itemId) - 1 
        if (index >= 0 && index < section.items.length) {
          itemIndex = index
        }
      }
      
      if (itemIndex === -1) {
        itemIndex = section.items.findIndex((item, index) => {
          const generatedId = `${sectionKey.toLowerCase()}_${index + 1}`
          return generatedId === itemId
        })
      }
      
      if (itemIndex !== -1) {
        const currentItem = section.items[itemIndex]
        
        switch (sectionKey) {
          case 'Founders':
            section.items[itemIndex] = {
              ...currentItem,
              name: newData.name || currentItem.name,
              quote: newData.default_value || currentItem.quote
            }
            break
          case 'Testimonials':
            // eslint-disable-next-line no-case-declarations
            const [author, company] = (newData.name || `${currentItem.author} - ${currentItem.company}`).split(' - ')
            section.items[itemIndex] = {
              ...currentItem,
              author: author || currentItem.author,
              company: company || currentItem.company,
              quote: newData.default_value || currentItem.quote
            }
            break
          case 'WhyBCSPro':
            section.items[itemIndex] = {
              ...currentItem,
              title: newData.name || currentItem.title,
              subtitle: newData.default_value || currentItem.subtitle
            }
            break
          default:
            section.items[itemIndex] = {
              ...currentItem,
              title: newData.name || currentItem.title || currentItem.name,
              subtitle: newData.default_value || currentItem.subtitle,
              quote: newData.default_value || currentItem.quote
            }
        }
      } else {
        handleErrors(`Item with ID "${itemId}" not found in ${sectionKey} items. Available items:`, section.items.map((item, index) => ({ index, id: item.id, name: item.name || item.title })))
      }
    } else {
      const sectionKey = path[1]
      const elementKey = path[2]
      
      const section = landingPageData.value[sectionKey]
      if (section && section['Labels - Subtitles'] && section['Labels - Subtitles'][elementKey]) {
        section['Labels - Subtitles'][elementKey] = {
          ...section['Labels - Subtitles'][elementKey],
          ...newData
        }
      } else {
        let current = landingPageData.value
        for (let i = 1; i < path.length - 1; i++) {
          if (current[path[i]]) {
            current = current[path[i]]
          }
        }
        const finalKey = path[path.length - 1]
        if (current && current[finalKey]) {
          current[finalKey] = {
            ...current[finalKey],
            ...newData
          }
        }
      }
    }
    
    createFlattenedLandingPage()
  } else {
    let current = bcsData.value
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]]
    }
    const finalKey = path[path.length - 1]
    if (current[finalKey]) {
      current[finalKey] = {
        ...current[finalKey],
        ...newData
      }
    }
    createFlattenedCopies()
  }
}

function handleCancelEdit() {
  copyModalRef.value.closeModal()
}

function handleCreateFaq() {
  createFaqForm.value = {
    title: '',
    body: '',
    sectionKey: faqSections.value[0]?.questionTitle || ''
  }
  copyModalMode.value = 'createFaq'
  copyModalRef.value.openModal()
}

async function handleSaveFaqCreate() {
  if (!isCreateFaqFormValid.value) return

  try {
    createFaqSaving.value = true

    const targetSectionKey = createFaqForm.value.sectionKey
    const targetSection = faqSections.value.find(s => s.questionTitle === targetSectionKey)

    if (!targetSection) {
      handleErrors('Section not found')
      return
    }

    const existingIds = targetSection.FaqList.map(faq => parseInt(faq.MIGX_id) || 0)
    const newMigxId = String(existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1)

    const newFaq = {
      MIGX_id: newMigxId,
      faqTitle: createFaqForm.value.title,
      faqBody: createFaqForm.value.body
    }

    targetSection.FaqList.push(newFaq)

    await saveCopy(`FAQS/${targetSectionKey}`, { FaqList: JSON.stringify(targetSection.FaqList) })

    remoteFaqs.value[targetSectionKey].FaqList = JSON.stringify(targetSection.FaqList)
    createFlattenedFaqs()

    copyModalRef.value.closeModal()
    successModalRef.value.openModal()
  } catch (error) {
    handleErrors('Error creating FAQ:', error)
  } finally {
    createFaqSaving.value = false
  }
}

function handleCancelCreateFaq() {
  copyModalRef.value.closeModal()
}

function handleView(copy, path) {
  selectedCopy.value = {
    ...copy.data,
    path: getPathString(path),
    default_value: copy.data.default_value || ''
  }
  copyModalMode.value = 'view'
  copyModalRef.value.openModal()
}

function stripHtml(html) {
  if (!html) return ''
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

function truncateText(text, maxLength = 100) {
  if (!text) return ''
  let cleanText = text.replace(/<br\s*\/?>/gi, '\n')
  const tmp = document.createElement('div')
  tmp.innerHTML = cleanText
  cleanText = tmp.textContent || tmp.innerText || ''
  return cleanText
}

function getPathString(path) {
  return path.join(' > ')
}

function handleSearch({ results }) {
  searchResults.value = results
  isSearchActive.value = true
}

function handleClear() {
  searchResults.value = []
  isSearchActive.value = false
}

function handleSelect(copy) {
  if (copy && copy.path) {
    if (copy.path.length > 0) {
      expandedCategories.value.add(copy.path[0])
    }
    
    if (copy.path.length > 1) {
      for (let i = 1; i < copy.path.length; i++) {
        const subPath = copy.path.slice(0, i + 1)
        expandedSubcategories.value.add(subPath.join('.'))
      }
    }
  }
}

function filterCategoriesBySearch(categories) {
  if (!searchResults.value.length) return []
  const resultIds = new Set(searchResults.value.map(result => result.id))
  function filterCategory(category) {
    const filteredCopies = category.copies.filter(copy => {
      return resultIds.has(copy.data.id)
    })
    const filteredSubcategories = category.subcategories
      .map(filterCategory)
      .filter(subcat => subcat.copies.length > 0 || subcat.subcategories.length > 0)
    return {
      ...category,
      copies: filteredCopies,
      subcategories: filteredSubcategories,
      totalCopies: filteredCopies.length + filteredSubcategories.reduce((sum, sub) => sum + sub.totalCopies, 0)
    }
  }
  return categories
    .map(filterCategory)
    .filter(cat => cat.copies.length > 0 || cat.subcategories.length > 0)
}

function handleDeleteFaq(copy) {
  deletingFaq.value = copy
  notificationModalMode.value = 'deleteConfirm'
  successModalRef.value.openModal()
}

async function handleConfirmDelete() {
  if (!deletingFaq.value) return

  try {
    deleteInProgress.value = true
    const faqId = deletingFaq.value.data.id

    const sectionKey = deletingFaq.value.data.questionTitle
    const targetSection = faqSections.value.find(s => s.questionTitle === sectionKey)
    const targetFaqIndex = targetSection ? targetSection.FaqList.findIndex(faq => faq.MIGX_id === faqId) : -1

    if (!targetSection || targetFaqIndex === -1) {
      handleErrors('FAQ not found')
      return
    }

    targetSection.FaqList.splice(targetFaqIndex, 1)

    targetSection.FaqList.forEach((faq, index) => {
      faq.MIGX_id = String(index + 1)
    })

    await saveCopy(`FAQS/${targetSection.questionTitle}`, { FaqList: JSON.stringify(targetSection.FaqList) })

    remoteFaqs.value[targetSection.questionTitle].FaqList = JSON.stringify(targetSection.FaqList)
    createFlattenedFaqs()

    notificationModalMode.value = 'success'
  } catch (error) {
    handleErrors('Error deleting FAQ:', error)
  } finally {
    deleteInProgress.value = false
    deletingFaq.value = null
  }
}

function handleCancelDelete() {
  successModalRef.value.closeModal()
  deletingFaq.value = null
}

function handleNotificationModalClose() {
  if (notificationModalMode.value === 'deleteConfirm') {
    setTimeout(() => {
      notificationModalMode.value = 'success'
    }, 350)
  }
}

onMounted(() => {
  getTemplateVariables()
})
</script>

<template>
  <div class="pe-4 me-0">
    <div class="w-100 d-flex title-tab-container justify-content-between me-4">        
    </div>
    
    <!-- Search component -->                
    <div class="search-container mx-4 mb-4">
      <BcsSearchDropdown 
        :truncateContent="50" 
        placeholder="Search copy by content" 
        :items="allUniqueItems" 
        :searchFields="['default_value']" 
        :maxSuggestions="10" 
        @search="handleSearch" 
        :arrayTerm="'default_value'" 
        @select="handleSelect" 
        tooltipText="Use the search function to find the existing text you’re looking to update"
        :show-arrow="true" 
        @clear="handleClear" />
    </div>

    <!-- Loading state -->
    <div v-if="bcsLoading" class="text-center p-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2">Loading BCS data...</p>
    </div>

    <!-- Error state -->
    <div v-else-if="bcsError" class="alert alert-danger mx-4" role="alert">
      <i class="bi bi-exclamation-triangle"></i>
      {{ bcsError }}
    </div>

    <div v-else-if="categorizedData.length > 0" class="categories-container mx-4">    
      <div class="hierarchy-container">
        <div 
          v-for="category in categorizedData" 
          :key="category.key"
          class="category-section mb-4">
          
          <!-- Main Category header -->
          <div class="category-header" @click="toggleCategory(category.name)">
            <div class="d-flex align-items-center justify-content-between p-3  border rounded">
              <div class="d-flex align-items-center">
                <i class="bi bi-folder me-2 text-primary fs-5"></i>
                <strong class="fs-7">{{ category.name }}</strong>
                <i class="bi ms-2" :class="expandedCategories.has(category.name) ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
              </div>
              <div class="d-flex gap-2 align-items-center">
                <button
                  v-if="category.name === 'FAQs'"
                  @click.stop="handleCreateFaq()"
                  class="btn btn-primary btn-sm"
                  title="Add new FAQ entry">
                  <i class="bi bi-plus-lg"></i> New
                </button>
                <span class="badge bg-primary">{{ category.totalCopies }} entries</span>
                <span v-if="category.subcategories.length > 0" class="badge bg-dark">{{ category.subcategories.length }} subcategories</span>
              </div>
            </div>
          </div>
          
          <!-- Category content -->
          <div v-show="expandedCategories.has(category.name)" class="category-content mt-2">
            
            <!-- Direct copies in main category -->
            <div v-if="category.copies.length > 0" class="copies-section mb-3">
              <div class="table-responsive">
                <table class="table table-hover table-sm">
                  <thead class="table-light">
                    <tr>
                      <th scope="col" style="width: 15%;">ID</th>
                      <th scope="col" style="width: 20%;">Name</th>
                      <th scope="col" style="width: 45%;">Content</th>
                      <th scope="col" style="width: 20%;">{{ category.name === 'FAQs' ? 'Section ID' : (category.name === 'Glossary' ? 'Subtitle' : (category.name === 'Landing Page' ? 'Section' : 'Caption')) }}</th>
                      <th scope="col" style="width: 10%;">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="copy in category.copies" :key="copy.key">
                      <td>
                        <div class="small fw-bold id-copy">{{ copy.data.id || 'N/A' }}</div>
                      </td>
                      <td>
                        <strong class="text-primary">{{ copy.data.name || copy.key }}</strong>
                      </td>
                      <td>
                        <div 
                          class="content-preview" 
                          :title="stripHtml(copy.data.default_value)">
                          {{ copy.data.default_value ? truncateText(copy.data.default_value, 150) : 'No content' }}
                        </div>
                      </td>
                      <td>
                        <span class="text-primary">
                          {{ copy.data.caption || (category.name === 'FAQs' ? 'No section ID' : (category.name === 'Glossary' ? 'No subtitle' : (category.name === 'Landing Page' ? 'No section' : 'No caption'))) }}
                        </span>
                      </td>
                      <td>
                        <div class="btn-group btn-group-sm">
                          <button 
                            @click="handleView(copy, copy.path)"
                            class="btn btn-outline-primary btn-sm"
                            title="View ">
                            <i class="bi bi-eye"></i>
                          </button>
                          <button 
                            @click="handleEdit(copy, copy.path)"
                            class="btn btn-outline-secondary btn-sm"
                            title="Edit">
                            <i class="bi bi-pencil"></i>
                          </button>
                          <button
                            v-if="category.name === 'FAQs'"
                            @click="handleDeleteFaq(copy)"
                            class="btn btn-outline-danger btn-sm"
                            title="Delete">
                            <i class="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <!-- Subcategories -->
            <div v-if="category.subcategories.length > 0" class="subcategories-section">
              <div 
                v-for="subcategory in category.subcategories" 
                :key="subcategory.key"
                class="subcategory-section mb-3 ms-3">
                
                <!-- Subcategory header -->
                <div class="subcategory-header" @click="toggleSubcategory(subcategory.path)">
                  <div class="d-flex align-items-center justify-content-between p-2 bg-white border rounded">
                    <div class="d-flex align-items-center">
                      <i class="bi bi-folder2 me-2 text-secondary"></i>
                      <strong>{{ subcategory.name }}</strong>
                      <i class="bi ms-2" :class="expandedSubcategories.has(subcategory.path.join('.')) ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
                    </div>
                    <span class="badge bg-light text-dark">{{ subcategory.totalCopies }} entries</span>
                  </div>
                </div>
                
                <!-- Subcategory copies -->
                <div v-show="expandedSubcategories.has(subcategory.path.join('.'))" class="subcategory-content mt-2 ms-3">
                  <div v-if="subcategory.copies.length > 0" class="table-responsive">
                    <table class="table table-hover table-sm">
                      <thead class="table-light">
                        <tr>
                          <th scope="col" style="width: 15%;">ID</th>
                          <th scope="col" style="width: 20%;">Name</th>
                          <th scope="col" style="width: 45%;">Content</th>
                          <th scope="col" style="width: 20%;">{{ category.name === 'FAQs' ? 'Section ID' : (category.name === 'Glossary' ? 'Subtitle' : (category.name === 'Landing Page' ? 'Section' : 'Caption')) }}</th>
                          <th scope="col" style="width: 10%;">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="copy in subcategory.copies" :key="copy.key">
                          <td>
                            <div class="small fw-bold id-copy">{{ copy.data.id || 'N/A' }}</div>
                          </td>
                          <td>
                            <strong class="text-primary">{{ copy.data.name || copy.key }}</strong>
                          </td>
                          <td>
                            <div 
                              class="content-preview" 
                              :title="stripHtml(copy.data.default_value)">
                              {{ copy.data.default_value ? truncateText(copy.data.default_value, 150) : 'No content' }}
                            </div>
                          </td>
                          <td>
                            <span class="text-primary">
                              {{ copy.data.caption || (category.name === 'FAQs' ? 'No section ID' : (category.name === 'Glossary' ? 'No subtitle' : (category.name === 'Landing Page' ? 'No section' : 'No caption'))) }}
                            </span>
                          </td>
                          <td>
                            <div class="btn-group btn-group-sm">
                              <button 
                                @click="handleView(copy, copy.path)"
                                class="btn btn-outline-primary btn-sm"
                                title="Ver detalles">
                                <i class="bi bi-eye"></i>
                              </button>
                              <button 
                                @click="handleEdit(copy, copy.path)"
                                class="btn btn-outline-secondary btn-sm"
                                title="Editar">
                                <i class="bi bi-pencil"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <!-- Nested subcategories if any -->
                  <div v-if="subcategory.subcategories.length > 0" class="nested-subcategories ms-3">
                    <div 
                      v-for="nestedSubcategory in subcategory.subcategories" 
                      :key="nestedSubcategory.key"
                      class="nested-subcategory-section mb-2 ms-2">
                      
                      <!-- Nested Subcategory header -->
                      <div class="nested-subcategory-header" @click="toggleSubcategory(nestedSubcategory.path)">
                        <div class="d-flex align-items-center justify-content-between p-2 bg-white border rounded">
                          <div class="d-flex align-items-center">
                            <i class="bi bi-folder2-open me-2 text-primary"></i>
                            <strong class="small">{{ nestedSubcategory.name }}</strong>
                            <i class="bi ms-2" :class="expandedSubcategories.has(nestedSubcategory.path.join('.')) ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
                          </div>
                          <span class="badge bg-primary small">{{ nestedSubcategory.totalCopies }} entries</span>
                        </div>
                      </div>
                      
                      <!-- Nested subcategory copies -->
                      <div v-show="expandedSubcategories.has(nestedSubcategory.path.join('.'))" class="nested-subcategory-content mt-2 ms-3">
                        <div v-if="nestedSubcategory.copies.length > 0" class="table-responsive">
                          <table class="table table-hover table-sm">
                            <thead >
                              <tr>
                                <th scope="col" style="width: 15%;">ID</th>
                                <th scope="col" style="width: 20%;">Name</th>
                                <th scope="col" style="width: 45%;">Content</th>
                                <th scope="col" style="width: 20%;">{{ category.name === 'FAQs' ? 'Section ID' : (category.name === 'Glossary' ? 'Subtitle' : (category.name === 'Landing Page' ? 'Section' : 'No caption')) }}</th>
                                <th scope="col" style="width: 10%;">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="copy in nestedSubcategory.copies" :key="copy.key">
                                <td>
                                  <div class="small fw-bold id-copy">{{ copy.data.id || 'N/A' }}</div>
                                </td>
                                <td>
                                  <strong class="text-primary">{{ copy.data.name || copy.key }}</strong>
                                </td>
                                <td>
                                  <div 
                                    class="content-preview" 
                                    :title="stripHtml(copy.data.default_value)">
                                    {{ copy.data.default_value ? truncateText(copy.data.default_value, 150) : 'No content' }}
                                  </div>
                                </td>
                                <td>
                                  <span class="text-primary">
                                    {{ copy.data.caption || (category.name === 'FAQs' ? 'No section ID' : (category.name === 'Glossary' ? 'No subtitle' : (category.name === 'Landing Page' ? 'No section' : 'No caption'))) }}
                                  </span>
                                </td>
                                <td>
                                  <div class="btn-group btn-group-sm">
                                    <button 
                                      @click="handleView(copy, copy.path)"
                                      class="btn btn-outline-primary btn-sm"
                                      title="Ver detalles">
                                      <i class="bi bi-eye"></i>
                                    </button>
                                    <button 
                                      @click="handleEdit(copy, copy.path)"
                                      class="btn btn-outline-secondary btn-sm"
                                      title="Editar">
                                      <i class="bi bi-pencil"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Copy Modal (view / edit / create FAQ) -->
  <BcsModal
    ref="copyModalRef"
    modalType="info"
    :modalTitle="copyModalMode === 'view' ? 'Copy Details' : copyModalMode === 'edit' ? 'Edit Copy' : 'New FAQ Entry'"
    modalSize="medium"
    :hideCloseButton="false"
  >
    <!-- View mode -->
    <template v-if="copyModalMode === 'view' && selectedCopy">
      <div class="copy-details">
        <div class="mb-3">
          <div class="text-primary mb-1 subtitle-modal">ID</div>
          <p>{{ selectedCopy.id || 'N/A' }}</p>
        </div>
        <div class="mb-3">
          <div class="text-primary mb-1 subtitle-modal">Name</div>
          <p>{{ selectedCopy.name }}</p>
        </div>
        <div class="mb-3">
          <div class="text-primary mb-1 subtitle-modal">Caption</div>
          <p>{{ selectedCopy.caption || 'Sin caption' }}</p>
        </div>
        <div class="mb-3">
          <div class="text-primary mb-1 subtitle-modal">Path</div>
          <p>{{ selectedCopy.path }}</p>
        </div>
        <div class="mb-3">
          <div class="text-primary mb-1 subtitle-modal">Content</div>
          <div class="content-box p-3 border rounded bg-light">
            <div v-if="selectedCopy.default_value" class="full-content" v-html="selectedCopy.default_value"></div>
            <div v-else class="text-muted">No content</div>
          </div>
        </div>
      </div>
    </template>

    <!-- Edit mode -->
    <template v-else-if="copyModalMode === 'edit' && editingCopy">
      <div class="edit-copy-form">
        <form @submit.prevent="handleSaveEdit">
          <div class="mb-3">
            <label class="form-label text-primary subtitle-modal">ID</label>
            <input type="text" class="form-control" v-model="editForm.id" readonly disabled>
          </div>
          <div class="mb-3">
            <label class="form-label text-primary subtitle-modal">Name</label>
            <input type="text" class="form-control" v-model="editForm.name" :readonly="editingCopy.path[0] !== 'FAQs'" :disabled="editingCopy.path[0] !== 'FAQs'">
          </div>
          <div class="mb-3">
            <label class="form-label text-primary subtitle-modal">Caption</label>
            <input type="text" class="form-control" v-model="editForm.caption">
          </div>
          <div class="mb-3">
            <label class="form-label text-primary subtitle-modal">Path</label>
            <input type="text" class="form-control" :value="getPathString(editingCopy.path)" readonly disabled>
          </div>
          <div class="mb-3">
            <label class="form-label text-primary subtitle-modal">Content</label>
            <textarea class="form-control content-textarea" v-model="editForm.default_value" rows="10" placeholder="Enter content here..."></textarea>
          </div>
          <div class="d-flex justify-content-end gap-2 mt-4">
            <button type="button" class="btn btn-secondary" @click="handleCancelEdit" :disabled="editSaving">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="editSaving || !isFormValid">
              <span v-if="editSaving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ editSaving ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
        </form>
      </div>
    </template>

    <!-- Create FAQ mode -->
    <template v-else-if="copyModalMode === 'createFaq'">
      <div class="edit-copy-form">
        <form @submit.prevent="handleSaveFaqCreate">
          <div class="mb-3">
            <label class="form-label text-primary subtitle-modal">Section</label>
            <select class="form-select" v-model="createFaqForm.sectionKey" required>
              <option v-for="section in faqSections" :key="section.questionTitle" :value="section.questionTitle">
                {{ section.sectionName || section.questionTitle }}
              </option>
            </select>
          </div>
          <div class="mb-3">
            <label class="form-label text-primary subtitle-modal">Title <span class="text-danger">*</span></label>
            <input type="text" class="form-control" v-model="createFaqForm.title" placeholder="Enter FAQ title..." required>
            <div v-if="createFaqForm.title.trim() === ''" class="form-text text-danger">Title is required.</div>
          </div>
          <div class="mb-3">
            <label class="form-label text-primary subtitle-modal">Body <span class="text-danger">*</span> <small class="text-muted fw-normal">(HTML allowed)</small></label>
            <textarea class="form-control content-textarea" v-model="createFaqForm.body" rows="10" placeholder="Enter FAQ body content. HTML tags are supported..." required></textarea>
            <div v-if="createFaqForm.body.trim() === ''" class="form-text text-danger">Body is required.</div>
          </div>
          <div class="d-flex justify-content-end gap-2 mt-4">
            <button type="button" class="btn btn-outline-primary" @click="handleCancelCreateFaq" :disabled="createFaqSaving">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="createFaqSaving || !isCreateFaqFormValid">
              <span v-if="createFaqSaving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ createFaqSaving ? 'Saving...' : 'Create' }}
            </button>
          </div>
        </form>
      </div>
    </template>
  </BcsModal>

  <!-- Shared Notification Modal (delete confirm + success) -->
  <BcsModal
    ref="successModalRef"
    :modalType="notificationModalMode === 'deleteConfirm' ? 'info' : 'success'"
    :modalTitle="notificationModalMode === 'deleteConfirm' ? 'Delete FAQ Entry' : ''"
    modalSize="small"
    :hideCloseButton="false"
    customClass="success-modal"
    @closeButtonClicked="handleNotificationModalClose"
  >
    <div v-if="notificationModalMode === 'deleteConfirm'" class="text-center py-2">
      <i class="bi bi-exclamation-triangle-fill text-danger fs-1 mb-3 d-block"></i>
      <p class="mb-1">Are you sure you want to delete this FAQ entry?</p>
      <p class="fw-bold text-danger mb-3">{{ deletingFaq?.data?.name }}</p>
      <p class="text-muted small mb-4">This action cannot be undone.</p>
      <div class="d-flex justify-content-center gap-2">
        <button
          type="button"
          class="btn btn-outline-primary"
          @click="handleCancelDelete"
          :disabled="deleteInProgress">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          @click="handleConfirmDelete"
          :disabled="deleteInProgress">
          <span v-if="deleteInProgress" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          {{ deleteInProgress ? 'Deleting...' : 'Delete' }}
        </button>
      </div>
    </div>
    <div v-else class="text-center success-content">
      <h4 class="text-success mb-2">Copy Updated Successfully!</h4>
      <p class="text-muted">Your changes have been saved and updated in real-time.</p>
    </div>
  </BcsModal>
</template>

<style scoped lang="scss">
.title-tab-container {
  padding: 20px 0;
}

.categories-container {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  padding: 1rem;
}

.category-header,
.subcategory-header {
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.category-section {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.subcategory-section {
  border: 1px solid #f1f3f4;
  border-radius: 6px;
  background-color: #fafbfc;
}

.table {
  margin-bottom: 0;
  font-size: 0.875rem;
}

.content-preview {
  font-size: 0.8rem;
  line-height: 1.4;
  color: #6c757d;
  max-height: 60px;
  overflow: hidden;
  white-space: pre-wrap;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3; 
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
}

.btn-group-sm .btn {
  padding: 0.2rem 0.4rem;
}

.badge {
  font-size: 0.7rem;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.hierarchy-container {
  max-height: 80vh;
  overflow-y: auto;
}

.copy-details {
  text-align: left;
}

.full-content {
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
}

.subtitle-modal{
    font-size: 16px;   
    font-weight: 500; 
}

.edit-copy-form {
  text-align: left;
}

.content-textarea {
  min-height: 200px;
  resize: vertical;
  font-family: monospace;
  font-size: 14px;
  line-height: 1.5;
}

.form-control:disabled {
  background-color: #f8f9fa;
  opacity: 0.7;
}

.success-content {
  padding: 2rem 1rem;
  
  .bi-check-circle-fill {
    animation: successPulse 0.6s ease-in-out;
  }
  
  h4 {
    font-weight: 600;
  }
}


:deep {

    .medium {
        max-width: 85% !important;
    }

    .alert-content {
        margin-bottom: 0 !important;
    }

    .modal-content:not(.success-modal) {
        border-radius: 4px !important;
        height: auto;

        .modal-body {
        height: calc(100vh - 200px) !important;
        overflow-y: auto;
        }
    }
    
    .small {
        max-width: 400px !important;
    }
}
</style>